








































































































































import { Vue, Component, Ref, Watch } from "vue-property-decorator";
import { TextConfiguration, Alignment } from "@/models/types";
import { customizationStore, onboardingStore } from "@/store/typed";
import Throttler from "@/helpers/Throttler";
import { showWarning } from "@/helpers/Toast";
import { PrintArea } from "@/models";
import {
  FONTS,
  COLORS,
  COLOR_WHEEL_URL,
  COLORS_ICON_URL,
  RED_BAR_URL,
} from "@/constants";
import VIcon from "@/components/Common/VIcon.vue";
import { isMobile } from "@/helpers/mediaQueryHelper";
import { Chrome } from "vue-color";

const PICKER_HEIGHT = 270;

export type FeatureType = "alignment" | "font" | "color";

@Component({ components: { VIcon, Chrome } })
export default class TextOverlay extends Vue {
  private selectedFeature: FeatureType = "font";
  private text = "";
  private color = "#ffffff";
  private font = "Poppins";
  private alignment: Alignment = "center";
  private visible = false;
  private new = false;
  private object: any = null;
  private canvasPickerMode: any = false;
  private editorInitialHeight = 0;
  private editorHeight = 0;
  private pickedColorConfig = {
    color: null,
    position: { x: 0, y: 0 },
    chosenColor: null,
  };

  private controlsStyle = {};
  private overlayStyle = {};
  private pickerStyle: any = { left: 0, top: 0 };
  private openColorSelector = false;
  private emojiThrottler = new Throttler();

  @Ref() readonly textarea: any;
  @Ref() readonly overlay: any;
  @Ref() readonly colorSelectorTrigger: any;

  private canvas: any;

  get showTextOnboarding() {
    return this.colorSelected && onboardingStore.textOnboarding;
  }

  get colorWheel() {
    return COLOR_WHEEL_URL;
  }

  get printArea(): PrintArea {
    return customizationStore.printArea;
  }

  get colorIcon() {
    return COLORS_ICON_URL;
  }

  get redBarUrl() {
    return RED_BAR_URL;
  }

  onColorsClicked() {
    onboardingStore.setTextOnboarding(false);
  }

  viewportHandler(event) {
    this.afterResize(event.target.height);
  }

  mounted() {
    this.editorHeight = window.innerHeight;
    this.afterResize(window.innerHeight);
    if (this.isMobile) {
      this.subscribeToResize();
    }
  }

  afterResize(height: number) {
    if (this.editorHeight === 0) {
      return;
    }
    this.editorHeight = height;
    let offset = 0;
    if (this.fontSelected || this.colorSelected) {
      offset = 77;
    }
    this.controlsStyle = { top: this.editorHeight - offset + "px" };
    this.overlayStyle = { top: this.editorHeight - (offset + 16) + "px" };
  }

  destroyed() {
    this.unsubscribeFromResize();
  }

  get isMobile() {
    return isMobile((this as any).$mq);
  }

  get alignmentIcon() {
    return `align-${this.alignment}`;
  }

  get alignmentClass() {
    return `text-${this.alignment}`;
  }

  get id() {
    return this.object?.id || null;
  }

  get fontSelected() {
    return this.selectedFeature === "font";
  }

  get pickedColor() {
    return this.pickedColorConfig.color;
  }

  get pickedAndSelectedColor() {
    return this.pickedColorConfig.chosenColor;
  }

  get canvasPickerIndicatorStyle() {
    return {
      background: this.pickedAndSelectedColor,
      top: `${this.pickedColorConfig?.position?.y || 0}px`,
      left: `${this.pickedColorConfig?.position?.x || 0}px`,
    };
  }

  subscribeToResize() {
    window.visualViewport.addEventListener("scroll", this.viewportHandler);
    window.visualViewport.addEventListener("resize", this.viewportHandler);
  }

  unsubscribeFromResize() {
    window.visualViewport.removeEventListener("scroll", this.viewportHandler);
    window.visualViewport.removeEventListener("resize", this.viewportHandler);
  }

  toggleCanvasPickerMode() {
    this.canvasPickerMode = !this.canvasPickerMode;
    if (this.canvasPickerMode) {
      this.overlay.addEventListener("mousemove", this.onMouseMove);
    } else {
      this.overlay.removeEventListener("mousemove", this.onMouseMove);
    }
  }

  @Watch("colorSelected")
  onColorSelectedChanged(value: any) {
    if (value) {
      this.$nextTick(() => {
        const { top, left } = this.getDocumentOffsetPosition(
          this.$refs.colorSelectorTrigger
        );
        this.pickerStyle = {
          top: `${top - PICKER_HEIGHT}px`,
          left: `${left}px`,
        };
      });
      this.afterResize(window.innerHeight);
    }
  }

  @Watch("fontSelected")
  onFontSelectedChanged(value: any) {
    if (value) {
      this.afterResize(window.innerHeight);
    }
  }

  getDocumentOffsetPosition(el: any) {
    let top = 0,
      left = 0;
    while (el !== null) {
      top += el?.offsetTop || 0;
      left += el?.offsetLeft || 0;
      el = el?.offsetParent || null;
    }
    return { top, left };
  }

  onMouseMove(event) {
    if (!this.canvasPickerMode) {
      return;
    }
    const context = this.canvas.getContext("2d");
    const rect = this.canvas.getBoundingClientRect();
    if (this.eventOutsideCanvas(event)) {
      return;
    }
    const positionX = event.clientX - rect.x;
    const positionY = event.clientY - rect.y;
    const multiplier = window.devicePixelRatio;
    const rawColor = context.getImageData(
      positionX * multiplier,
      positionY * multiplier,
      1,
      1
    ).data;
    this.pickedColorConfig.color = `#${this.rgbToHex(
      rawColor[0],
      rawColor[1],
      rawColor[2]
    )}`;
  }

  destroy() {
    this.overlay.removeEventListener("mousemove", this.onMouseMove);
  }

  get colorSelected() {
    return this.selectedFeature === "color";
  }

  get fonts() {
    return FONTS;
  }

  get alignments() {
    return ["center", "left", "right", "justify"];
  }

  get rgbColorProfile() {
    const printAreaCustomization =
      customizationStore.currentPrintAreaCustomization;
    return printAreaCustomization.colorProfile != "greyscale";
  }

  get colors() {
    const printAreaCustomization =
      customizationStore.currentPrintAreaCustomization;
    if (this.rgbColorProfile) {
      return COLORS;
    } else {
      return ["#FFFFFF", "#000000"];
    }
  }

  eventOutsideCanvas(event) {
    const rect = this.canvas.getBoundingClientRect();
    return (
      event.clientY < rect.y ||
      event.clientY > rect.y + rect.height ||
      event.clientX < rect.x ||
      event.clientX > rect.x + rect.width
    );
  }

  rgbToHex(r: number, g: number, b: number) {
    return this.valueToHex(r) + this.valueToHex(g) + this.valueToHex(b);
  }

  valueToHex(color: number) {
    var hexadecimal = color.toString(16);
    return hexadecimal.length == 1 ? "0" + hexadecimal : hexadecimal;
  }

  openColorPicker(event: any) {
    (this.$refs.colorPicker as any).toggleColorBlockOverlay(event);
  }

  fontPreviewStyle(font: string) {
    return { fontFamily: font };
  }

  colorPreviewStyle(color: string) {
    return { background: color };
  }

  show(canvas: any, data: TextConfiguration | null) {
    this.canvas = canvas.getElement();
    const choosingColor = this.printArea.defaultTextColor;
    this.object = data;
    this.new = data === null;
    this.text = data?.text || "";
    this.color = data?.fill || choosingColor;
    this.font = data?.fontFamily || "Poppins";
    this.visible = true;
    this.$nextTick(() => {
      this.textarea.focus();
    });
  }

  removeEmojis(text: string) {
    if (!text) {
      return "";
    }

    const emojiRegex =
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g;

    const replacedString = text.replace(emojiRegex, "");
    if (replacedString != text) {
      this.emojiThrottler.run(() => {
        showWarning(this.$t("warnings.use_our_emojis").toString());
      });
    }
    return replacedString;
  }

  @Watch("text")
  onTextChanged(text: string, oldText: string) {
    if (text === oldText) {
      return;
    }

    this.text = this.removeEmojis(text);
  }

  get fontStyle() {
    return {
      fontFamily: this.font,
      color: this.color,
    };
  }

  get isVisible() {
    return this.visible;
  }

  onFontChanged(font: string) {
    this.font = font;
    this.object?.set({ fontFamily: font });
  }

  onAlignmentClicked() {
    const index = this.alignments.findIndex(
      (o: string) => o === this.alignment
    );
    this.alignment = this.alignments[
      (index + 1) % this.alignments.length
    ] as Alignment;
    this.object?.set({ textAlign: this.alignment });
  }

  onColorSelected(event: any) {
    const offset = this.getDocumentOffsetPosition(this.overlay);

    if (
      this.canvasPickerMode &&
      this.pickedColorConfig.color &&
      !this.eventOutsideCanvas(event)
    ) {
      this.pickedColorConfig.chosenColor = this.pickedColorConfig.color;
      this.onColorChanged(this.pickedColorConfig.color, false);
      this.pickedColorConfig.position = {
        x: event.clientX - offset.left,
        y: event.clientY - offset.top,
      };
    }
  }

  onColorChanged(color: string, staticColor = true) {
    this.openColorSelector = false;
    if (staticColor) {
      this.canvasPickerMode = false;
      this.pickedColorConfig.color = null;
      this.pickedColorConfig.chosenColor = null;
    }
    this.color = color;
    this.object?.set({ fill: color });
  }

  onColorPicked(color: string, staticColor = true) {
    if (staticColor) {
      this.canvasPickerMode = false;
      this.pickedColorConfig.color = null;
      this.pickedColorConfig.chosenColor = null;
    }
    this.color = color;
    this.object?.set({ fill: color });
  }

  close() {
    this.visible = false;
    this.$emit("close", this.object);
    this.reset();
  }

  done() {
    this.visible = false;
    this.$emit("done", {
      text: this.text,
      fontFamily: this.font,
      fill: this.color,
      textAlign: this.alignment,
      id: this.id,
    });
    this.reset();
  }

  reset() {
    this.text = "";
    this.object = null;
    this.canvasPickerMode = false;
    this.selectedFeature = "font";
  }
}
